import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Heart, MessageSquare } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Seo } from "@/components/Seo";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { name, email, subject, message });
      toast.success("Thanks — your message has been sent!");
      setName(""); setEmail(""); setSubject(""); setMessage("");
    } catch {
      toast.error("Something went wrong sending that. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <Seo
        title="Contact"
        description="Get in touch with My Theatre with feedback, suggestions or questions about West End shows, cast changes and ticket prices."
      />
      <PageHero
        offset={4}
        overline="We'd love to hear from you"
        title="Get in touch"
        callout={{
          icon: MessageSquare,
          lead: "A young project with a big goal.",
          text: "We want to become the place theatre-goers come to first — one hub where you can find every show, compare ticket prices across sellers, and keep track of the cast and news that matter to you.",
        }}
      />

      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <p className="text-muted-foreground">
          We're just getting started, and every feature on this site so far has come from thinking about
          what would genuinely make theatre-going easier. That's why your feedback matters so much —
          tell us what's missing, what's confusing, or what you wish existed, and we'll do our best to
          build it. This site is shaped by the people who use it.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-xl border border-input bg-card px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Email</label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-input bg-card px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Subject</label>
            <input
              required
              maxLength={150}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What is this about?"
              className="w-full rounded-xl border border-input bg-card px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Message</label>
            <textarea
              required
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Suggestions, feedback, something not working — let us know…"
              className="w-full rounded-xl border border-input bg-card px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[#B82332] disabled:opacity-60"
          >
            <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send message"}
          </button>
        </form>

        <p className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Heart className="h-4 w-4 text-primary" /> Thanks for helping shape My Theatre.
        </p>
      </div>
    </div>
  );
}